import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, TextHeading, Button, useModalContext, CloseButtonSpacer, CloseButton, useMediaSize, Box, } from '@ally/metronome-ui';
import { space } from 'styled-system';
import TimelineTracker from '../TimelineTracker';
import StatusModalDescription from './StatusModalDescription';
import StatusModalDetails from './StatusModalDetails';
import { ConfirmNavigationModal } from './ConfirmNavigationModal';
const StyledTextHeading = styled(TextHeading)(space);
const StyledModal = styled(Modal) `
  ${({ theme: { media } }) => `
    > div {
      ${media.down('sm')} {
        padding: 0;
      }
    }
  `}
`;
function getTitle(title) {
    switch (title) {
        case 'WireTransfer':
            return 'Wire Transfer';
        case 'CheckOrder':
            return 'Check Order';
        case 'DebitCardOrder':
            return 'Debit Card';
        default:
            return 'Transfer';
    }
}
function getTriggerContent({ currentStatus, title, hasContactUs, isDebitUnfunded, }) {
    const isAch = title === 'ACHTransfer';
    if (hasContactUs)
        return 'Action required';
    if (isDebitUnfunded)
        return 'Needs attention';
    switch (currentStatus) {
        case 'Confirmed':
            return 'Ordered';
        case 'InProcess':
            return isAch ? 'Processing' : 'Ordered';
        case 'VerificationRequired':
            return 'Needs attention';
        case 'Completed':
        case 'Posted':
            return 'Delivered';
        case 'Delayed':
            return 'Delivery delayed';
        case 'DeliveryFailed':
            return 'Delivery unsuccessful';
        default:
            return currentStatus;
    }
}
const Trigger = ({ event, isWiresMigrationEnabled }) => {
    const { setOpen } = useModalContext();
    const { activityName, eventModelData, statuses } = event;
    const currentStatus = statuses[0].status;
    const { isFunded } = eventModelData;
    const { contactUsFlag } = eventModelData;
    const statusTrackerType = activityName === 'InternalTransfers'
        ? 'Internal Transfers'
        : getTitle(activityName);
    const hasContactUs = isWiresMigrationEnabled && contactUsFlag;
    const isDebitUnfunded = activityName === 'DebitCardOrder' &&
        currentStatus === 'Ordered' &&
        !isFunded;
    const triggerContent = getTriggerContent({
        currentStatus,
        title: activityName,
        hasContactUs,
        isDebitUnfunded,
    });
    const withAllyTMContent = `${statusTrackerType.split(' ')[0]}-${triggerContent.split(' ').join('-')}`;
    const onClick = () => {
        setOpen(true);
        // Tracking modal for analytics
        /* istanbul ignore next: not testing window events */
        if (window.allytm) {
            ;
            window.allytm.event('modalView', `AOS:Status Tracker:${statusTrackerType}:${triggerContent}`, {
                treatAsPageview: true,
                statusTrackerType,
                statusTrackerStatus: `${currentStatus}`,
            });
        }
    };
    return (React.createElement(Button, { variant: "link", text: triggerContent, onClick: onClick, allytmln: withAllyTMContent }));
};
const headingRef = React.createRef();
const confirmationTriggerRef = React.createRef();
export const ModalContent = ({ event, isStandaloneModal, isWiresMigrationEnabled, confirmModal, setConfirmModal, isDebitCardEnabled, isDebitCardControlHidden, }) => {
    const [focusRef, setFocusRef] = useState(headingRef);
    const { activityName } = event;
    const { externalLink, showConfirmModal } = confirmModal;
    const { setOpen } = useModalContext();
    const resetConfirmModal = () => {
        // New pattern caused modal in modal, When confirmation modal is closed return focus to previous modal trigger
        setFocusRef(confirmationTriggerRef);
        setConfirmModal({
            externalLink: '',
            showConfirmModal: false,
        });
    };
    const isSmDown = useMediaSize('SmDown');
    React.useEffect(() => {
        // setTimeout needed to wait for metronome focus to be handled first before manually setting focus
        setTimeout(() => {
            var _a;
            if (!showConfirmModal) {
                (_a = focusRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        });
    }, [focusRef, showConfirmModal]);
    return (React.createElement(React.Fragment, null,
        React.createElement(CloseButtonSpacer, { isFullScreenSmDown: isSmDown }),
        React.createElement(Box, { p: isSmDown ? 'md' : 'none' }, showConfirmModal ? (React.createElement(ConfirmNavigationModal, { resetConfirmModal: resetConfirmModal, url: externalLink })) : (React.createElement(React.Fragment, null,
            !isStandaloneModal && (React.createElement(StyledTextHeading, { tag: "h1", size: "md", mb: "md", pr: "lg", tabIndex: -1, ref: headingRef }, getTitle(activityName))),
            React.createElement(TimelineTracker, { event: event, isWiresMigrationEnabled: isWiresMigrationEnabled }),
            React.createElement(StatusModalDescription, { event: event, isStandaloneModal: isStandaloneModal, isWiresMigrationEnabled: isWiresMigrationEnabled, setConfirmModal: setConfirmModal, confirmationTriggerRef: confirmationTriggerRef, isDebitCardEnabled: isDebitCardEnabled, isDebitCardControlHidden: isDebitCardControlHidden }),
            React.createElement(StatusModalDetails, { event: event, isDebitCardEnabled: isDebitCardEnabled, isStandaloneModal: isStandaloneModal })))),
        React.createElement(CloseButton, { onClick: () => showConfirmModal ? resetConfirmModal() : setOpen(false), "data-testid": "close-modal", "data-track-elem": "icon", "data-track-name": "Close", "data-track-trigger": "close" })));
};
const StatusModal = ({ event, isStandaloneModal = false, isWiresMigrationEnabled = false, isDebitCardEnabled = false, isDebitCardControlHidden = true, }) => {
    const [confirmModal, setConfirmModal] = useState({
        externalLink: '',
        showConfirmModal: false,
    });
    return (React.createElement(StyledModal, { trigger: React.createElement(Trigger, { event: event, isWiresMigrationEnabled: isWiresMigrationEnabled }), content: React.createElement(ModalContent, { event: event, isStandaloneModal: isStandaloneModal, isWiresMigrationEnabled: isWiresMigrationEnabled, confirmModal: confirmModal, setConfirmModal: setConfirmModal, isDebitCardEnabled: isDebitCardEnabled, isDebitCardControlHidden: isDebitCardControlHidden }), height: "auto", hideCloseButton: true, disableCloseOnOverlayClick: confirmModal.showConfirmModal }));
};
export default StatusModal;
//# sourceMappingURL=index.js.map